
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Editor } from '@tinymce/tinymce-react';

interface NewTermsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const NewTermsDialog = ({ open, onOpenChange, onSuccess }: NewTermsDialogProps) => {
  const { toast } = useToast();
  const [version, setVersion] = React.useState('');
  const [content, setContent] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [editorKey, setEditorKey] = React.useState<string>('');

  React.useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-tinymce-key');
        if (error) throw error;
        setEditorKey(data.apiKey);
      } catch (error) {
        console.error('Failed to fetch TinyMCE API key:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to initialize the editor. Please try again later."
        });
      }
    };

    fetchApiKey();
  }, [toast]);

  const handleEditorChange = (content: string) => {
    setContent(content);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('terms_of_service')
        .insert({
          version,
          content,
          published_at: new Date().toISOString(),
          is_current: false
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "New terms of service version created successfully."
      });
      
      onSuccess();
      onOpenChange(false);
      setVersion('');
      setContent('');
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not create new terms version."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[825px]">
        <DialogHeader>
          <DialogTitle>Create New Terms of Service Version</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="version">Version Number</Label>
            <Input
              id="version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g., 1.0"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="content">Terms Content</Label>
            <div className="border rounded-md">
              {editorKey && (
                <Editor
                  apiKey={editorKey}
                  init={{
                    height: 400,
                    menubar: true,
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'charmap',
                      'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'paste', 'help', 'wordcount'
                    ],
                    toolbar: 'undo redo | blocks | ' +
                      'bold italic | alignleft aligncenter alignright | bullist numlist | ' +
                      'removeformat | help',
                    content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif; font-size: 14px; }' +
                      'ul, ol { margin-left: 1.5em; padding-left: 1.5em; }' +
                      'ul > li { list-style-type: disc; margin-bottom: 0.5em; }' +
                      'ol > li { list-style-type: decimal; margin-bottom: 0.5em; }',
                    formats: {
                      alignleft: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li', styles: { textAlign: 'left' } },
                      aligncenter: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li', styles: { textAlign: 'center' } },
                      alignright: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li', styles: { textAlign: 'right' } }
                    },
                    list_styles: true
                  }}
                  value={content}
                  onEditorChange={handleEditorChange}
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Create Version
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
